import React, { useState } from "react";
import useSWR from "swr";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, Plus, Pencil, Trash2, Compass, Globe2, Camera, Award, Phone, Mail } from "lucide-react";
import { toast } from "sonner";

const fetcher = (u) => api.get(u).then((r) => r.data);

const empty = {
  first_name: "", last_name: "", email: "", phone: "", country: "",
  languages: "", specialties: "", certifications: "",
  day_rate: 0, currency: "USD", active: true, gender: "", notes: "",
};

export default function Guides() {
  const { data, mutate } = useSWR("/guides", fetcher);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const openNew = () => {
    setEditId(null);
    setForm(empty);
    setOpen(true);
  };

  const openEdit = (g) => {
    setEditId(g.id);
    setForm({
      first_name: g.first_name || "",
      last_name: g.last_name || "",
      email: g.email || "",
      phone: g.phone || "",
      country: g.country || "",
      languages: g.languages || "",
      specialties: g.specialties || "",
      certifications: g.certifications || "",
      day_rate: g.day_rate ?? 0,
      currency: g.currency || "USD",
      active: g.active ?? true,
      gender: g.gender || "",
      notes: g.notes || "",
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.first_name?.trim() || !form.last_name?.trim() || !form.email?.trim()) {
      return toast.error("Nombre, apellidos y email son obligatorios");
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        day_rate: Number(form.day_rate) || 0,
        gender: form.gender || "unspecified",
      };
      if (editId) {
        await api.patch(`/guides/${editId}`, payload);
        toast.success("Guía actualizado");
      } else {
        await api.post("/guides", payload);
        toast.success("Guía añadido");
      }
      setForm(empty);
      setEditId(null);
      setOpen(false);
      mutate();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (g) => {
    if (!confirm(`¿Eliminar a ${g.first_name} ${g.last_name}?`)) return;
    try {
      await api.delete(`/guides/${g.id}`);
      toast.success("Guía eliminado");
      mutate();
    } catch (e) {
      toast.error("No se pudo eliminar");
    }
  };

  const filtered = (data || []).filter((g) => {
    const s = `${g.first_name} ${g.last_name} ${g.email} ${g.country || ""} ${g.specialties || ""} ${g.languages || ""}`.toLowerCase();
    return q === "" || s.includes(q.toLowerCase());
  });

  const activeCount = (data || []).filter((g) => g.active !== false).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Guías</h1>
          <p className="text-slate-500 mt-1">
            {data?.length || 0} en total · <span className="text-emerald-600 font-medium">{activeCount} activos</span>
          </p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditId(null); setForm(empty); } }}>
          <DialogTrigger asChild>
            <Button data-testid="new-guide-btn" className="bg-[#026FCB] hover:bg-[#0358a5]" onClick={openNew}>
              <Plus size={16} className="mr-2" />Nuevo guía
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editId ? "Editar guía" : "Nuevo guía"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nombre *</Label>
                <Input data-testid="guide-first" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              </div>
              <div>
                <Label>Apellidos *</Label>
                <Input data-testid="guide-last" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              </div>
              <div>
                <Label>Email *</Label>
                <Input data-testid="guide-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <Label>País</Label>
                <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
              </div>
              <div>
                <Label>Idiomas</Label>
                <Input placeholder="Español, Inglés…" value={form.languages} onChange={(e) => setForm({ ...form, languages: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Especialidades</Label>
                <Input placeholder="Fauna, paisaje, astrofotografía…" value={form.specialties} onChange={(e) => setForm({ ...form, specialties: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Certificaciones</Label>
                <Input placeholder="Guía UIAGM, primeros auxilios, permisos…" value={form.certifications} onChange={(e) => setForm({ ...form, certifications: e.target.value })} />
              </div>
              <div>
                <Label>Tarifa por día</Label>
                <Input type="number" data-testid="guide-rate" value={form.day_rate} onChange={(e) => setForm({ ...form, day_rate: e.target.value })} />
              </div>
              <div>
                <Label>Moneda</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["USD", "EUR", "GBP", "MXN", "CLP", "ARS"].map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Género (para emparejar en doble)</Label>
                <Select value={form.gender || "unspecified"} onValueChange={(v) => setForm({ ...form, gender: v === "unspecified" ? "" : v })}>
                  <SelectTrigger data-testid="guide-gender"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unspecified">No especificado</SelectItem>
                    <SelectItem value="male">Hombre</SelectItem>
                    <SelectItem value="female">Mujer</SelectItem>
                    <SelectItem value="other">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Estado</Label>
                <Select
                  value={form.active ? "yes" : "no"}
                  onValueChange={(v) => setForm({ ...form, active: v === "yes" })}
                >
                  <SelectTrigger data-testid="guide-active"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Activo</SelectItem>
                    <SelectItem value="no">Inactivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Notas</Label>
                <Input placeholder="Disponibilidad, contacto agencia…" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button
                data-testid="guide-save"
                className="bg-[#026FCB] hover:bg-[#0358a5]"
                onClick={submit}
                disabled={saving}
              >
                {saving ? "Guardando…" : editId ? "Guardar cambios" : "Añadir guía"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="guides-search"
              className="pl-9"
              placeholder="Buscar por nombre, país, especialidad, idioma…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {filtered.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center text-slate-500">
            <Compass size={32} className="mx-auto mb-3 text-slate-400" />
            {data?.length ? "Ningún guía coincide con la búsqueda." : "Aún no hay guías. Pulsa "}
            {!data?.length && <b>+ Nuevo guía</b>}
            {!data?.length && " para dar de alta el primero."}
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((g) => (
            <Card
              key={g.id}
              data-testid={`guide-card-${g.id}`}
              className={`group transition-all hover:border-[#026FCB] ${g.active === false ? "opacity-60" : ""}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 rounded-full bg-[#026FCB]/10 flex items-center justify-center shrink-0">
                        <Compass size={16} className="text-[#026FCB]" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{g.first_name} {g.last_name}</div>
                        {g.country && (
                          <div className="text-xs text-slate-500 flex items-center gap-1">
                            <Globe2 size={11} />{g.country}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className={`text-[10px] mono uppercase tracking-wider px-1.5 py-0.5 rounded ${
                      g.active !== false
                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                        : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
                    }`}>
                      {g.active !== false ? "Activo" : "Inactivo"}
                    </span>
                  </div>
                </div>

                <div className="mt-3 space-y-1.5 text-sm">
                  <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300 min-w-0">
                    <Mail size={12} className="text-slate-400 shrink-0" />
                    <span className="truncate">{g.email}</span>
                  </div>
                  {g.phone && (
                    <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                      <Phone size={12} className="text-slate-400" />
                      <span className="mono">{g.phone}</span>
                    </div>
                  )}
                  {g.languages && (
                    <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                      <Globe2 size={12} className="text-slate-400" />
                      <span className="text-xs">{g.languages}</span>
                    </div>
                  )}
                  {g.specialties && (
                    <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                      <Camera size={12} className="text-slate-400" />
                      <span className="text-xs">{g.specialties}</span>
                    </div>
                  )}
                  {g.certifications && (
                    <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                      <Award size={12} className="text-slate-400" />
                      <span className="text-xs">{g.certifications}</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 flex items-center justify-between border-t border-slate-100 dark:border-[#25334D]/50 pt-3">
                  <div>
                    <div className="text-[10px] mono uppercase text-slate-500 tracking-wider">Tarifa día</div>
                    <div className="mono font-semibold">{money(g.day_rate || 0, g.currency)}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8"
                      data-testid={`edit-guide-${g.id}`}
                      onClick={() => openEdit(g)}
                    >
                      <Pencil size={13} className="mr-1" />Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 text-slate-400 hover:text-red-500"
                      data-testid={`del-guide-${g.id}`}
                      onClick={() => remove(g)}
                      title="Eliminar"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
                {g.notes && (
                  <div className="mt-3 text-xs text-slate-500 italic border-l-2 border-slate-200 dark:border-[#25334D] pl-2">
                    {g.notes}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
